/**
 * AnalyticsPage - Full-Featured Financial Analytics Dashboard
 *
 * Comprehensive analytics matching the design mockup:
 * - Cash Flow Score (health indicator with circular progress)
 * - Spending by Category (donut chart with legend)
 * - Month-over-Month comparison (side-by-side cards)
 * - Pay Cycles (income sources and schedules)
 * - Upcoming Cash Flow (timeline projection)
 * - Recurring Bills (monthly bills by due date)
 * - Insights & Warnings (smart recommendations)
 * - Monthly Trends (bar chart)
 * - Daily Balance (area chart)
 *
 * All sections are configurable - can be toggled on/off.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useAnalytics } from '@hooks/useAnalytics';
import { useSummary } from '@hooks/useSummary';
import { useRecurring } from '@hooks/useRecurring';
import { useWatchlist } from '@hooks/useWatchlist';
import { usePrivacyMode } from '@hooks/usePrivacyMode';
import { WatchlistSection } from '@/components/analytics/WatchlistSection';
import { CategoryDrilldown } from '@/components/analytics/CategoryDrilldown';
import { Card, PrivacyToggle } from '@/components/shared';
import { DateRangePicker, calculateDateRange, loadDateRangePreset, saveDateRangePreset, type DateRange } from '@/components/DateRangePicker';
import { generateInsights, type Insight } from '@services/insightsService';
import { buildCapitalMoveMatcher } from '@services/watchlist';
import { db } from '@services/db';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
  LineChart,
  Line,
  ReferenceDot,
} from 'recharts';
import {
  loadAnalyticsSettings,
  saveAnalyticsSettings,
  ANALYTICS_SECTIONS,
  type AnalyticsSectionVisibility,
} from '@services/analyticsSettings';
import {
  formatCurrency as formatCurrencyIntl,
  formatCurrencyCompact as formatCurrencyCompactIntl,
  formatMonthDay,
  formatMonthYear,
} from '@/utils/format';
import { useLanguage, categoryDisplayName, t as i18nT } from '@/i18n';
import './AnalyticsPage.css';

// ============================================================
// Helpers
// ============================================================

function formatCompact(amount: number): string {
  // Locale-aware compact currency ("$1.2K") - BUG-074 rule, no inline '$'
  return formatCurrencyCompactIntl(amount, 'USD');
}

function formatCurrency(amount: number): string {
  const hasCents = Math.abs(amount - Math.trunc(amount)) >= 0.005;
  // Central locale-aware formatter (utils/format.ts) - never inline Intl here
  return formatCurrencyIntl(amount, 'USD', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  });
}

function MaskedAmount() {
  return <span className="private-amount masked">••••</span>;
}

function MoneyValue({
  amount,
  privacy,
  signed = false,
  prefix = '',
}: {
  amount: number;
  privacy: boolean;
  signed?: boolean;
  prefix?: string;
}) {
  if (privacy) return <>{prefix}<MaskedAmount /></>;

  const sign = signed && amount > 0 ? '+' : '';
  return <>{prefix}{sign}{formatCurrency(amount)}</>;
}

function maskMoneyText(text: string, privacy: boolean): string {
  if (!privacy) return text;
  return text.replace(/\$-?[\d,]+(?:\.\d+)?/g, '••••');
}

function formatPercent(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(0)}%`;
}

function getPreviousMonthLabel(monthKey?: string): string {
  if (!monthKey) {
    const now = new Date();
    return formatMonthYear(new Date(now.getFullYear(), now.getMonth() - 1));
  }

  const [year, month] = monthKey.split('-').map(Number);
  return formatMonthYear(new Date(year, month - 2, 1));
}

interface CategoryDisplayItem {
  categoryId: string;
  categoryName: string;
  categoryColor: string;
  amount: number;
  percentage: number;
  transactionCount?: number;
  categoryIds?: string[];      // Real category ids behind this bucket (drill-down)
  isUncategorized?: boolean;   // Bucket of missing/orphaned-category txns
}

function summarizeCategoryDisplay(categories: CategoryDisplayItem[], limit = 6): CategoryDisplayItem[] {
  if (categories.length <= limit) return categories;

  const visible = categories.slice(0, limit - 1);
  const hidden = categories.slice(limit - 1);
  const otherAmount = hidden.reduce((sum, cat) => sum + cat.amount, 0);
  const otherPct = hidden.reduce((sum, cat) => sum + cat.percentage, 0);
  const otherCount = hidden.reduce((sum, cat) => sum + (cat.transactionCount || 0), 0);

  return [
    ...visible,
    {
      categoryId: '__other_categories__',
      categoryName: i18nT('analytics.pie.other'),
      categoryColor: '#515866',
      amount: otherAmount,
      percentage: otherPct,
      transactionCount: otherCount,
      // Aggregate the hidden buckets' real category ids so drill-down works.
      // (If the uncategorized bucket got folded in here, its txns have no id
      // and are not listed - the drill-down list covers real categories.)
      categoryIds: hidden.flatMap((c) => c.categoryIds || []),
    },
  ];
}

function calculateCashFlowScore(income: number, expense: number): number {
  if (income === 0) return 0;
  const savingsRate = (income - expense) / income;
  const baseScore = Math.min(100, Math.max(0, 50 + savingsRate * 250));
  const bonus = income > expense ? 10 : 0;
  return Math.min(100, Math.round(baseScore + bonus));
}

/**
 * Parse income schedule string and return next expected pay date.
 * Handles: "Every 1st & 15th", "Every 2 weeks", "Monthly (15th)", "Weekly"
 */
function getNextIncomeDate(schedule: string): Date {
  const now = new Date();
  const currentDay = now.getDate();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  // "Every 1st & 15th" - semi-monthly
  if (schedule.includes('1st & 15th') || schedule.includes('1st and 15th')) {
    if (currentDay < 1) return new Date(currentYear, currentMonth, 1);
    if (currentDay < 15) return new Date(currentYear, currentMonth, 15);
    return new Date(currentYear, currentMonth + 1, 1);
  }

  // "Monthly (15th)" - extract day number
  const monthlyMatch = schedule.match(/Monthly\s*\((\d+)/i);
  if (monthlyMatch) {
    const day = parseInt(monthlyMatch[1], 10);
    if (currentDay < day) return new Date(currentYear, currentMonth, day);
    return new Date(currentYear, currentMonth + 1, day);
  }

  // "Every 2 weeks" - biweekly, estimate next Friday
  if (schedule.includes('2 weeks') || schedule.toLowerCase().includes('biweekly')) {
    const daysUntilFriday = (5 - now.getDay() + 7) % 7;
    const nextFriday = daysUntilFriday === 0 ? 7 : daysUntilFriday;
    return new Date(currentYear, currentMonth, currentDay + nextFriday);
  }

  // "Weekly" - next week same day
  if (schedule.toLowerCase().includes('weekly')) {
    return new Date(currentYear, currentMonth, currentDay + 7);
  }

  // Default: 1st of next month
  return new Date(currentYear, currentMonth + 1, 1);
}

/**
 * Parse bill dayOfMonth string and return next due date.
 * Handles: "1st", "15th", "10th-26th" (range), "Various"
 */
function getNextBillDate(dayOfMonth: string): Date {
  const now = new Date();
  const currentDay = now.getDate();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  // "1st", "15th", "23rd" - single day
  const singleDayMatch = dayOfMonth.match(/^(\d+)(st|nd|rd|th)?$/i);
  if (singleDayMatch) {
    const day = parseInt(singleDayMatch[1], 10);
    if (currentDay < day) return new Date(currentYear, currentMonth, day);
    return new Date(currentYear, currentMonth + 1, day);
  }

  // "10th-26th" - range, use the start day
  const rangeMatch = dayOfMonth.match(/(\d+)(st|nd|rd|th)?\s*-\s*(\d+)/i);
  if (rangeMatch) {
    const startDay = parseInt(rangeMatch[1], 10);
    if (currentDay < startDay) return new Date(currentYear, currentMonth, startDay);
    return new Date(currentYear, currentMonth + 1, startDay);
  }

  // "Various" or unknown - default to 15th
  if (currentDay < 15) return new Date(currentYear, currentMonth, 15);
  return new Date(currentYear, currentMonth + 1, 15);
}

function getScoreStatus(score: number): { label: string; color: string; description: string } {
  if (score >= 80) {
    return {
      label: i18nT('analytics.score.healthy.label'),
      color: 'var(--income)',
      description: i18nT('analytics.score.healthy.desc'),
    };
  }
  if (score >= 60) {
    return {
      label: i18nT('analytics.score.good.label'),
      color: 'var(--accent)',
      description: i18nT('analytics.score.good.desc'),
    };
  }
  if (score >= 40) {
    return {
      label: i18nT('analytics.score.fair.label'),
      color: 'var(--warning)',
      description: i18nT('analytics.score.fair.desc'),
    };
  }
  return {
    label: i18nT('analytics.score.needs_work.label'),
    color: 'var(--expense)',
    description: i18nT('analytics.score.needs_work.desc'),
  };
}

// ============================================================
// Sub-Components
// ============================================================

function CashFlowScore({ score }: { score: number }) {
  const status = getScoreStatus(score);
  const circumference = 2 * Math.PI * 45;
  const progress = (score / 100) * circumference;

  return (
    <div className="health-card">
      <div className="health-score">
        <div className="health-ring">
          <svg viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="var(--bg-hover)"
              strokeWidth="8"
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke={status.color}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference - progress}
              transform="rotate(-90 50 50)"
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
          </svg>
          <div className="health-value" style={{ color: status.color }}>{score}</div>
        </div>
        <div className="health-label">{i18nT('analytics.score_label')}</div>
      </div>
      <div className="health-status">
        <span className={`status-badge status-badge--${score >= 80 ? 'good' : score >= 60 ? 'okay' : score >= 40 ? 'warning' : 'danger'}`}>
          {status.label}
        </span>
        <p className="health-summary">{status.description}</p>
      </div>
    </div>
  );
}

function AnalyticsSummaryCards({
  income,
  expense,
  net,
  transactionCount,
  monthsCovered,
  periodLabel,
  isPrivacyMode,
  capitalMoves,
}: {
  income: number;
  expense: number;
  net: number;
  transactionCount: number;
  monthsCovered: number;
  periodLabel: string;
  isPrivacyMode: boolean;
  capitalMoves: { expense: number; income: number; count: number };
}) {
  const savingsRate = income > 0 ? ((income - expense) / income) * 100 : null;

  return (
    <div className="analytics-summary-grid" aria-label={i18nT('analytics.summary.aria', { period: periodLabel })}>
      <div className="analytics-summary-card">
        <span className="summary-kicker">{i18nT('common.income')}</span>
        <span className="summary-value amount--income">
          <MoneyValue amount={income} privacy={isPrivacyMode} />
        </span>
        <span className="summary-note">{periodLabel}</span>
      </div>
      <div className="analytics-summary-card">
        <span className="summary-kicker">{i18nT('common.expenses')}</span>
        <span className="summary-value amount--expense">
          <MoneyValue amount={expense} privacy={isPrivacyMode} />
        </span>
        <span className="summary-note">
          {capitalMoves.count > 0
            ? isPrivacyMode
              ? i18nT('analytics.summary.capital_excluded_hidden')
              : i18nT('analytics.summary.capital_excluded', {
                  amount: formatCurrency(capitalMoves.expense),
                })
            : i18nT('analytics.summary.real_spending')}
        </span>
      </div>
      <div className="analytics-summary-card">
        <span className="summary-kicker">{i18nT('analytics.summary.net')}</span>
        <span className={`summary-value ${net >= 0 ? 'amount--income' : 'amount--expense'}`}>
          <MoneyValue amount={net} privacy={isPrivacyMode} signed />
        </span>
        <span className="summary-note">{net >= 0 ? i18nT('analytics.summary.saved') : i18nT('analytics.summary.overspent')}</span>
      </div>
      <div className="analytics-summary-card">
        <span className="summary-kicker">{i18nT('analytics.summary.savings_rate')}</span>
        <span className={`summary-value ${savingsRate !== null && savingsRate >= 0 ? 'amount--income' : 'amount--expense'}`}>
          {savingsRate === null ? i18nT('analytics.summary.na') : `${savingsRate.toFixed(0)}%`}
        </span>
        <span className="summary-note">{i18nT('analytics.summary.txn_months', { count: transactionCount, months: monthsCovered })}</span>
      </div>
    </div>
  );
}

interface MonthComparison {
  current: { income: number; expense: number; net: number; label: string };
  previous: { income: number; expense: number; net: number; label: string };
}

function MonthOverMonth({ data }: { data: MonthComparison }) {
  const { isPrivacyMode } = usePrivacyMode();

  const incomeChange = data.previous.income > 0
    ? ((data.current.income - data.previous.income) / data.previous.income) * 100
    : 0;
  const expenseChange = data.previous.expense > 0
    ? ((data.current.expense - data.previous.expense) / data.previous.expense) * 100
    : 0;
  const netChange = data.previous.net !== 0
    ? ((data.current.net - data.previous.net) / Math.abs(data.previous.net)) * 100
    : 0;

  const incomeDiff = data.current.income - data.previous.income;
  const expenseDiff = data.current.expense - data.previous.expense;
  const netDiff = data.current.net - data.previous.net;

  return (
    <div className="mom-section">
      <div className="mom-cards">
        <div className="mom-card mom-current">
          <div className="mom-header">
            <span className="mom-month">{data.current.label}</span>
            <span className="mom-badge">{i18nT('analytics.mom.current')}</span>
          </div>
          <div className="mom-stats">
            <div className="mom-stat">
              <span className="mom-stat-label">{i18nT('common.income')}</span>
              <span className="mom-stat-value amount--income">
                <MoneyValue amount={data.current.income} privacy={isPrivacyMode} />
              </span>
            </div>
            <div className="mom-stat">
              <span className="mom-stat-label">{i18nT('common.expenses')}</span>
              <span className="mom-stat-value amount--expense">
                <MoneyValue amount={data.current.expense} privacy={isPrivacyMode} />
              </span>
            </div>
            <div className="mom-stat">
              <span className="mom-stat-label">{i18nT('analytics.summary.net')}</span>
              <span className={`mom-stat-value ${data.current.net >= 0 ? 'amount--income' : 'amount--expense'}`}>
                <MoneyValue amount={data.current.net} privacy={isPrivacyMode} signed />
              </span>
            </div>
          </div>
        </div>

        <div className="mom-vs">{i18nT('analytics.mom.vs')}</div>

        <div className="mom-card mom-previous">
          <div className="mom-header">
            <span className="mom-month">{data.previous.label}</span>
            <span className="mom-badge mom-badge-dim">{i18nT('analytics.mom.previous')}</span>
          </div>
          <div className="mom-stats">
            <div className="mom-stat">
              <span className="mom-stat-label">{i18nT('common.income')}</span>
              <span className="mom-stat-value">
                <MoneyValue amount={data.previous.income} privacy={isPrivacyMode} />
              </span>
            </div>
            <div className="mom-stat">
              <span className="mom-stat-label">{i18nT('common.expenses')}</span>
              <span className="mom-stat-value">
                <MoneyValue amount={data.previous.expense} privacy={isPrivacyMode} />
              </span>
            </div>
            <div className="mom-stat">
              <span className="mom-stat-label">{i18nT('analytics.summary.net')}</span>
              <span className="mom-stat-value">
                <MoneyValue amount={data.previous.net} privacy={isPrivacyMode} signed />
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mom-changes">
        <div className={`change-item ${incomeDiff >= 0 ? 'change-item--up' : 'change-item--down'}`}>
          <span className="change-icon">{incomeDiff >= 0 ? '↑' : '↓'}</span>
          <span className="change-text">
            {isPrivacyMode ? (
              <>{i18nT(incomeDiff >= 0 ? 'analytics.mom.income_up_hidden' : 'analytics.mom.income_down_hidden')} <MaskedAmount /></>
            ) : (
              i18nT(incomeDiff >= 0 ? 'analytics.mom.income_up' : 'analytics.mom.income_down', {
                amount: formatCurrency(Math.abs(incomeDiff)),
                percent: formatPercent(incomeChange),
              })
            )}
          </span>
        </div>
        <div className={`change-item ${expenseDiff <= 0 ? 'change-item--up' : 'change-item--down'}`}>
          <span className="change-icon">{expenseDiff >= 0 ? '↑' : '↓'}</span>
          <span className="change-text">
            {isPrivacyMode ? (
              <>{i18nT(expenseDiff >= 0 ? 'analytics.mom.expenses_up_hidden' : 'analytics.mom.expenses_down_hidden')} <MaskedAmount /></>
            ) : (
              i18nT(expenseDiff >= 0 ? 'analytics.mom.expenses_up' : 'analytics.mom.expenses_down', {
                amount: formatCurrency(Math.abs(expenseDiff)),
                percent: formatPercent(expenseChange),
              })
            )}
          </span>
        </div>
        <div className={`change-item ${netDiff >= 0 ? 'change-item--up' : 'change-item--down'}`}>
          <span className="change-icon">{netDiff >= 0 ? '↑' : '↓'}</span>
          <span className="change-text">
            {isPrivacyMode ? (
              <>{i18nT(netDiff >= 0 ? 'analytics.mom.net_up_hidden' : 'analytics.mom.net_down_hidden')} <MaskedAmount /></>
            ) : (
              i18nT(netDiff >= 0 ? 'analytics.mom.net_up' : 'analytics.mom.net_down', {
                amount: formatCurrency(Math.abs(netDiff)),
                percent: formatPercent(netChange),
              })
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

interface InsightData {
  type: 'warning' | 'tip' | 'success' | 'info';
  title: string;
  description: string;
}

function InsightCard({ insight }: { insight: InsightData }) {
  const { isPrivacyMode } = usePrivacyMode();
  const icons: Record<string, string> = {
    warning: '⚠️',
    tip: '💡',
    success: '✓',
    info: '📊',
  };

  return (
    <div className={`insight-card insight-card--${insight.type}`}>
      <div className="insight-icon">{icons[insight.type]}</div>
      <div className="insight-content">
        <div className="insight-title">{insight.title}</div>
        <div className="insight-desc">{maskMoneyText(insight.description, isPrivacyMode)}</div>
      </div>
    </div>
  );
}

// Pay Cycles Component
interface PayCycle {
  id: string;
  name: string;
  schedule: string;
  amount: number;
  icon: string;
  isEstimate?: boolean;
}

function PayCyclesSection({ payCycles, loading }: { payCycles: PayCycle[]; loading?: boolean }) {
  const { isPrivacyMode } = usePrivacyMode();

  if (loading) {
    return <div className="list-card"><div className="empty-state">{i18nT('analytics.pay_analyzing')}</div></div>;
  }

  if (payCycles.length === 0) {
    return (
      <div className="list-card">
        <div className="empty-state">
          <span className="empty-icon">💼</span>
          <span className="empty-text">{i18nT('analytics.pay_empty')}</span>
          <span className="empty-hint">{i18nT('analytics.pay_empty_hint')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="list-card">
      {payCycles.map((cycle) => (
        <div key={cycle.id} className="pay-cycle-row">
          <div className="pay-cycle-icon">{cycle.icon}</div>
          <div className="pay-cycle-info">
            <div className="pay-cycle-name">{cycle.name}</div>
            <div className="pay-cycle-schedule">{cycle.schedule}</div>
          </div>
          <div className="pay-cycle-amount amount--income">
            <MoneyValue amount={cycle.amount} privacy={isPrivacyMode} prefix={cycle.isEstimate ? '~' : ''} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Recurring Bills Component
interface RecurringBill {
  id: string;
  name: string;
  account: string;
  amount: number;
  dayOfMonth: string;
  isEstimate?: boolean;
}

function RecurringBillsSection({ bills, loading }: { bills: RecurringBill[]; loading?: boolean }) {
  const { isPrivacyMode } = usePrivacyMode();

  if (loading) {
    return <div className="list-card"><div className="empty-state">{i18nT('analytics.bills_analyzing')}</div></div>;
  }

  if (bills.length === 0) {
    return (
      <div className="list-card">
        <div className="empty-state">
          <span className="empty-icon">📋</span>
          <span className="empty-text">{i18nT('analytics.bills_empty')}</span>
          <span className="empty-hint">{i18nT('analytics.bills_empty_hint')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="list-card">
      {bills.map((bill) => (
        <div key={bill.id} className="bill-row">
          <div className="bill-day">{bill.dayOfMonth}</div>
          <div className="bill-info">
            <div className="bill-name">{bill.name}</div>
            <div className="bill-account">{bill.account}</div>
          </div>
          <div className="bill-amount amount--expense">
            <MoneyValue amount={bill.amount} privacy={isPrivacyMode} prefix={bill.isEstimate ? '~' : '-'} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Upcoming Cash Flow Timeline
interface CashFlowEvent {
  date: string;
  label: string;
  amount: number;
  type: 'income' | 'expense';
}

function UpcomingCashFlowSection({
  events,
  startingBalance,
}: {
  events: CashFlowEvent[];
  startingBalance: number;
}) {
  const { isPrivacyMode } = usePrivacyMode();

  // Generate timeline data
  const timelineData = useMemo(() => {
    const days = [];
    const now = new Date();
    let balance = startingBalance; // Use REAL account balance

    for (let i = 0; i < 21; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() + i);
      const dateStr = formatMonthDay(date);

      // Check for events on this day
      const dayEvents = events.filter(e => e.date === dateStr);
      for (const event of dayEvents) {
        balance += event.type === 'income' ? event.amount : -event.amount;
      }

      days.push({
        date: dateStr,
        balance,
        hasEvent: dayEvents.length > 0,
        eventType: dayEvents[0]?.type,
        eventLabel: dayEvents[0]?.label,
      });
    }
    return days;
  }, [events, startingBalance]);

  const projectionLow = useMemo(() => {
    return timelineData.reduce(
      (lowest, point) => point.balance < lowest.balance ? point : lowest,
      timelineData[0] || { date: '', balance: startingBalance }
    );
  }, [timelineData, startingBalance]);

  return (
    <div className="timeline-card">
      <div className="timeline-chart">
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={timelineData} margin={{ top: 10, right: 28, left: 10, bottom: 8 }}>
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }}
              interval={4}
            />
            <YAxis hide domain={['auto', 'auto']} />
            <Tooltip
              formatter={(value: number) => isPrivacyMode ? '••••' : formatCurrency(value)}
              contentStyle={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--r-md)',
                fontSize: '0.75rem',
              }}
            />
            <Line
              type="monotone"
              dataKey="balance"
              stroke="var(--income)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: 'var(--income)' }}
            />
            {events.map((event, idx) => {
              const dataPoint = timelineData.find(d => d.date === event.date);
              if (!dataPoint) return null;
              return (
                <ReferenceDot
                  key={idx}
                  x={event.date}
                  y={dataPoint.balance}
                  r={6}
                  fill={event.type === 'income' ? 'var(--income)' : 'var(--expense)'}
                  stroke="var(--bg-deep)"
                  strokeWidth={2}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Event Labels */}
      <div className="timeline-events">
        {events.slice(0, 3).map((event, idx) => (
          <div
            key={idx}
            className={`timeline-event ${event.type}`}
          >
            <span className="event-dot" />
            <span className="event-label">{event.label}</span>
            <span className="event-amount">
              <MoneyValue
                amount={event.amount}
                privacy={isPrivacyMode}
                prefix={event.type === 'income' ? '+' : '-'}
              />
            </span>
          </div>
        ))}
      </div>

      {/* Balance Projection */}
      <div className="timeline-footer">
        <div className="projection">
          <span className="proj-label">{i18nT('analytics.timeline.lowest')}</span>
          <span className="proj-value">
            <MoneyValue amount={projectionLow.balance} privacy={isPrivacyMode} />
            {projectionLow.date ? ` · ${projectionLow.date}` : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

export default function AnalyticsPage() {
  const { t, language } = useLanguage();
  const { isPrivacyMode } = usePrivacyMode();

  // Date range state - load saved preset from localStorage
  const [dateRange, setDateRange] = useState<DateRange>(() => calculateDateRange(loadDateRangePreset()));

  // Hooks - use date range for filtering
  const watchlist = useWatchlist({ start: dateRange.start, end: dateRange.end });

  // Capital-move matcher: watchlist groups flagged "not real spending"
  // (e.g. funds moved to an external Solana wallet recorded as expenses).
  // Analytics excludes those from spending stats; balances stay untouched.
  // Keyed on a SIGNATURE of the flagged groups' matching inputs - editing an
  // unrelated group field (e.g. typing a group name in the editor) must NOT
  // produce a new matcher, or useAnalytics refetches everything per keystroke.
  const capitalMoveSignature = useMemo(
    () =>
      JSON.stringify(
        watchlist.groups
          .filter((g) => g.enabled && g.excludeFromSpending)
          .map((g) => g.terms)
      ),
    [watchlist.groups]
  );
  const capitalMoveMatcher = useMemo(
    () => buildCapitalMoveMatcher(watchlist.groups),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [capitalMoveSignature]
  );
  const analyticsOptions = useMemo(() => ({ capitalMoveMatcher }), [capitalMoveMatcher]);

  const { data, loading, error, setDateRange: setAnalyticsDateRange } = useAnalytics(6, analyticsOptions);
  const { data: summary } = useSummary();
  const recurring = useRecurring(12); // Analyze last 12 months for better pattern detection

  // Account names cache for bill display
  const [accountNames, setAccountNames] = useState<Record<string, string>>({});

  const [showConfig, setShowConfig] = useState(false);
  const [visibility, setVisibility] = useState<AnalyticsSectionVisibility>(() =>
    loadAnalyticsSettings().visibility
  );

  // Category drill-down (donut slice / legend click). Closed on range change
  // - the selected bucket's numbers belong to the old period.
  const [drilldown, setDrilldown] = useState<CategoryDisplayItem | null>(null);
  useEffect(() => {
    setDrilldown(null);
  }, [dateRange.start, dateRange.end]);

  const toggleDrilldown = useCallback((cat: CategoryDisplayItem) => {
    setDrilldown((prev) => (prev?.categoryId === cat.categoryId ? null : cat));
  }, []);

  // Load account names for bill display
  useEffect(() => {
    async function loadAccountNames() {
      const accounts = await db.accounts.toArray();
      const names: Record<string, string> = {};
      for (const acc of accounts) {
        if (acc.id) {
          names[acc.id] = acc.name;
        }
      }
      setAccountNames(names);
    }
    loadAccountNames();
  }, []);

  // Update analytics when date range changes
  useEffect(() => {
    setAnalyticsDateRange({ start: dateRange.start, end: dateRange.end });
  }, [dateRange, setAnalyticsDateRange]);

  // Handle date range change - persist to localStorage
  const handleDateRangeChange = useCallback((newRange: DateRange) => {
    setDateRange(newRange);
    saveDateRangePreset(newRange.preset);
  }, []);

  // Cash flow score
  const cashFlowScore = useMemo(() => {
    return calculateCashFlowScore(data.totalIncome, data.totalExpense);
  }, [data.totalIncome, data.totalExpense]);

  const categorySpendingForDisplay = useMemo(() => {
    return summarizeCategoryDisplay(data.categorySpending, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.categorySpending, language]); // language: "Other categories" label is localized

  // Month-over-month data - NO FAKE DATA, only real values
  const momData = useMemo((): MonthComparison => {
    const trends = data.monthlyTrends;
    const now = new Date();
    const currentLabel = formatMonthYear(now);
    const prevDate = new Date(now.getFullYear(), now.getMonth() - 1);
    const previousLabel = formatMonthYear(prevDate);

    // Case 1: We have at least 2 months of trend data - use it
    if (trends.length >= 2) {
      const curr = trends[trends.length - 1];
      const prev = trends[trends.length - 2];
      return {
        current: { income: curr.income, expense: curr.expense, net: curr.net, label: curr.fullLabel || curr.month },
        previous: { income: prev.income, expense: prev.expense, net: prev.net, label: prev.fullLabel || prev.month },
      };
    }

    // Case 2: We have 1 month of trend data - use it for current, zeros for previous
    if (trends.length === 1) {
      const curr = trends[0];
      return {
        current: { income: curr.income, expense: curr.expense, net: curr.net, label: curr.fullLabel || curr.month },
        previous: { income: 0, expense: 0, net: 0, label: getPreviousMonthLabel(curr.monthKey) },
      };
    }

    // Case 3: No trend data at all - use summary for current month, zeros for previous
    // This is honest: we show what we know, not fabricated comparisons
    return {
      current: { income: summary.monthIncome, expense: summary.monthExpense, net: summary.monthNet, label: currentLabel },
      previous: { income: 0, expense: 0, net: 0, label: previousLabel },
    };
  }, [data.monthlyTrends, summary]);

  // Real pay cycles from transaction analysis
  const payCycles: PayCycle[] = useMemo(() => {
    if (recurring.loading || recurring.income.length === 0) {
      return []; // Show empty while loading or if no patterns detected
    }
    return recurring.income.map((inc) => ({
      id: inc.id,
      name: inc.name,
      schedule: inc.schedule,
      amount: inc.averageAmount,
      icon: inc.icon,
      isEstimate: inc.isEstimate,
    }));
  }, [recurring.income, recurring.loading]);

  // Real recurring bills from transaction analysis
  const recurringBills: RecurringBill[] = useMemo(() => {
    if (recurring.loading || recurring.bills.length === 0) {
      return []; // Show empty while loading or if no patterns detected
    }
    return recurring.bills.map((bill) => ({
      id: bill.id,
      name: bill.name,
      account: accountNames[bill.account] || i18nT('transactions.detail.na'),
      amount: bill.averageAmount,
      dayOfMonth: bill.dayOfMonth,
      isEstimate: bill.isEstimate,
    }));
  }, [recurring.bills, recurring.loading, accountNames]);

  // Real upcoming events based on detected patterns with actual dates
  const upcomingEvents: CashFlowEvent[] = useMemo(() => {
    const events: CashFlowEvent[] = [];

    // Add next income events using real schedule dates
    for (const inc of recurring.income.slice(0, 3)) {
      const nextDate = getNextIncomeDate(inc.schedule);
      events.push({
        date: formatMonthDay(nextDate),
        label: inc.name,
        amount: inc.averageAmount,
        type: 'income',
        _sortDate: nextDate, // For sorting
      } as CashFlowEvent & { _sortDate: Date });
    }

    // Add next bill events using real due dates
    for (const bill of recurring.bills.slice(0, 3)) {
      const nextDate = getNextBillDate(bill.dayOfMonth);
      events.push({
        date: formatMonthDay(nextDate),
        label: bill.name,
        amount: bill.averageAmount,
        type: 'expense',
        _sortDate: nextDate,
      } as CashFlowEvent & { _sortDate: Date });
    }

    // Sort by date (soonest first) and return top 4
    return events
      .sort((a, b) => {
        const dateA = (a as CashFlowEvent & { _sortDate?: Date })._sortDate?.getTime() || 0;
        const dateB = (b as CashFlowEvent & { _sortDate?: Date })._sortDate?.getTime() || 0;
        return dateA - dateB;
      })
      .slice(0, 4);
  }, [recurring.income, recurring.bills]);

  // Generate real insights from data
  const insights = useMemo((): InsightData[] => {
    // Uncategorized spending bucket (if any) for the nudge insight
    const uncategorizedBucket = data.categorySpending.find((c) => c.isUncategorized);

    // Use insights service to generate smart insights
    const generated = generateInsights({
      totalIncome: data.totalIncome,
      totalExpense: data.totalExpense,
      categorySpending: data.categorySpending.map((c) => ({
        categoryName: c.categoryName,
        amount: c.amount,
        percentage: c.percentage,
      })),
      monthlyTrends: data.monthlyTrends,
      currentBalance: summary.totalBalance,
      detectedBillsTotal: recurring.totalMonthlyBills,
      detectedIncomeTotal: recurring.totalMonthlyIncome,
      // Smart insights v2 inputs
      expenseCount: data.expenseCount,
      largestExpense: data.largestExpense,
      weekendExpense: data.weekendExpense,
      spendDays: data.spendDays,
      activeDays: data.activeDays,
      uncategorized: uncategorizedBucket
        ? { amount: uncategorizedBucket.amount, count: uncategorizedBucket.transactionCount || 0 }
        : null,
      watchlistTop: watchlist.topStats
        ? {
            name: watchlist.topStats.group.name,
            expense: watchlist.topStats.expense,
            count: watchlist.topStats.count,
            changePct: watchlist.topStats.changePct,
          }
        : null,
    });

    // Map to InsightData format
    return generated.map((insight: Insight) => ({
      type: insight.type,
      title: insight.title,
      description: insight.description,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, summary.totalBalance, recurring.totalMonthlyBills, recurring.totalMonthlyIncome, watchlist.topStats, language]);

  // Toggle visibility
  const toggleVisibility = useCallback((sectionId: keyof AnalyticsSectionVisibility) => {
    setVisibility(prev => {
      const newVisibility = { ...prev, [sectionId]: !prev[sectionId] };
      saveAnalyticsSettings({ visibility: newVisibility });
      return newVisibility;
    });
  }, []);

  // Chart colors - Darker, richer palette for dark theme
  const CHART_COLORS = [
    '#6b5b9a', // Deep Purple (Housing)
    '#c75050', // Deep Coral (Food)
    '#2a8a84', // Deep Teal (Shopping)
    '#b8941f', // Deep Gold (Transport)
    '#9a5070', // Deep Rose (Entertainment)
    '#3d4450', // Dark Slate (Other)
  ];

  if (error) {
    return (
      <div className="page-error">
        <h2>{t('analytics.load_failed')}</h2>
        <p>{error}</p>
      </div>
    );
  }

  const { categorySpending, monthlyTrends, totalExpense } = data;

  return (
    <div className="analytics-page">
      {/* Page Header */}
      <div className="analytics-header">
        <h1 className="page-title">{t('nav.analytics')}</h1>
        <div className="header-actions">
          <PrivacyToggle />
          <button
            className={`config-btn ${showConfig ? 'active' : ''}`}
            onClick={() => setShowConfig(!showConfig)}
            title={t('analytics.configure_aria')}
          >
            ⚙️
          </button>
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </div>
      </div>

      {/* Section Config Panel */}
      {showConfig && (
        <div className="config-panel">
          <div className="config-header">
            <span className="config-title">{t('analytics.config.title')}</span>
            <button className="config-close" onClick={() => setShowConfig(false)}>×</button>
          </div>
          <div className="config-grid">
            {ANALYTICS_SECTIONS.map((section) => (
              <label key={section.id} className="config-item">
                <input
                  type="checkbox"
                  checked={visibility[section.id]}
                  onChange={() => toggleVisibility(section.id)}
                />
                <span className="config-icon">{section.icon}</span>
                <span className="config-name">{t(`analytics.sections.${section.id}.name`)}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {!loading && (
        <AnalyticsSummaryCards
          income={data.totalIncome}
          expense={data.totalExpense}
          net={data.totalIncome - data.totalExpense}
          transactionCount={data.transactionCount}
          monthsCovered={data.monthsCovered}
          periodLabel={dateRange.label}
          isPrivacyMode={isPrivacyMode}
          capitalMoves={data.capitalMoves}
        />
      )}

      {/* Cash Flow Score */}
      {visibility.cashFlowScore && (
        <div className="section-wrapper">
          {loading ? (
            <div className="chart-loading">{t('analytics.calculating')}</div>
          ) : (
            <CashFlowScore score={cashFlowScore} />
          )}
        </div>
      )}

      {/* Spending by Category */}
      {visibility.spendingByCategory && (
        <>
          <div className="section-head">
            <span className="section-title">{t('analytics.sections.spendingByCategory.name')}</span>
            <span className="section-hint">{t('analytics.pie.click_hint')}</span>
          </div>
          <div className="pie-chart-card">
            {loading ? (
              <div className="chart-loading">{t('common.loading')}</div>
            ) : categorySpending.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">📊</span>
                <span className="empty-text">{t('analytics.pie.empty')}</span>
                <span className="empty-hint">{t('analytics.empty_range_hint')}</span>
              </div>
            ) : (
              <>
                <div className="pie-chart-container">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={categorySpendingForDisplay}
                        dataKey="amount"
                        nameKey="categoryName"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={1}
                        stroke="none"
                        onClick={(_, index) => {
                          const cat = categorySpendingForDisplay[index];
                          if (cat) toggleDrilldown(cat);
                        }}
                      >
                        {categorySpendingForDisplay.map((entry, index) => (
                          <Cell
                            key={entry.categoryId}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number) => isPrivacyMode ? '••••' : formatCurrency(value)}
                        contentStyle={{
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 'var(--r-md)',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="donut-center">
                    <div className="donut-total-label">{t('analytics.pie.total')}</div>
                    <div className="donut-total-value">
                      <MoneyValue amount={totalExpense} privacy={isPrivacyMode} />
                    </div>
                  </div>
                </div>
                <div className="pie-legend">
                  {categorySpendingForDisplay.map((cat, index) => (
                    <div
                      key={cat.categoryId}
                      className={`legend-row legend-row--clickable ${drilldown?.categoryId === cat.categoryId ? 'legend-row--selected' : ''}`}
                      onClick={() => toggleDrilldown(cat)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleDrilldown(cat);
                        }
                      }}
                    >
                      <span
                        className="legend-color"
                        style={{ background: CHART_COLORS[index % CHART_COLORS.length] }}
                      />
                      <span className="legend-name">{categoryDisplayName(cat.categoryName)}</span>
                      <span className="legend-value">
                        <MoneyValue amount={cat.amount} privacy={isPrivacyMode} />
                      </span>
                      <span className="legend-pct">{cat.percentage.toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          {drilldown && (
            <CategoryDrilldown
              category={{
                ...drilldown,
                categoryColor:
                  CHART_COLORS[
                    Math.max(0, categorySpendingForDisplay.findIndex((c) => c.categoryId === drilldown.categoryId)) %
                      CHART_COLORS.length
                  ],
              }}
              dateRange={dateRange}
              capitalMoveMatcher={capitalMoveMatcher}
              onClose={() => setDrilldown(null)}
            />
          )}
        </>
      )}

      {/* Watchlist - smart keyword tracker */}
      {visibility.watchlist && (
        <>
          <div className="section-head">
            <span className="section-title">{t('analytics.sections.watchlist.name')}</span>
            <span className="section-period">{dateRange.label}</span>
          </div>
          <WatchlistSection
            stats={watchlist.stats}
            groups={watchlist.groups}
            loading={watchlist.loading}
            onUpdateGroups={watchlist.updateGroups}
          />
        </>
      )}

      {/* Month-over-Month */}
      {visibility.monthOverMonth && (
        <>
          <div className="section-head">
            <span className="section-title">{t('analytics.sections.monthOverMonth.name')}</span>
          </div>
          {loading ? (
            <div className="chart-loading">{t('common.loading')}</div>
          ) : (
            <MonthOverMonth data={momData} />
          )}
        </>
      )}

      {/* Pay Cycles */}
      {visibility.payCycles && (
        <>
          <div className="section-head">
            <span className="section-title">{t('analytics.sections.payCycles.name')}</span>
          </div>
          <PayCyclesSection payCycles={payCycles} loading={recurring.loading} />
        </>
      )}

      {/* Upcoming Cash Flow */}
      {visibility.upcomingCashFlow && (
        <>
          <div className="section-head">
            <span className="section-title">{t('analytics.sections.upcomingCashFlow.name')}</span>
          </div>
          <UpcomingCashFlowSection
            events={upcomingEvents}
            startingBalance={summary.totalBalance}
          />
        </>
      )}

      {/* Recurring Bills */}
      {visibility.recurringBills && (
        <>
          <div className="section-head">
            <span className="section-title">{t('analytics.sections.recurringBills.name')}</span>
          </div>
          <RecurringBillsSection bills={recurringBills} loading={recurring.loading} />
        </>
      )}

      {/* Insights */}
      {visibility.insights && insights.length > 0 && (
        <>
          <div className="section-head">
            <span className="section-title">{t('analytics.sections.insights.name')}</span>
          </div>
          <div className="insights-grid">
            {insights.map((insight, index) => (
              <InsightCard key={index} insight={insight} />
            ))}
          </div>
        </>
      )}

      {/* Monthly Trends */}
      {visibility.monthlyTrends && (
        <>
          <div className="section-head">
            <span className="section-title">{t('analytics.sections.monthlyTrends.name')}</span>
          </div>
          <Card className="chart-card">
            <div className="chart-subtitle">{t('analytics.trends.subtitle')}</div>
            {loading ? (
              <div className="chart-loading">{t('common.loading')}</div>
            ) : monthlyTrends.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">📈</span>
                <span className="empty-text">{t('analytics.trends.empty')}</span>
                <span className="empty-hint">{t('analytics.trends.empty_hint')}</span>
              </div>
            ) : (
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={monthlyTrends} barGap={6} barCategoryGap="20%">
                    <defs>
                      <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4ecdc4" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="#2d8a82" stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ff6b6b" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="#b34a4a" stopOpacity={0.7} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }}
                      tickFormatter={isPrivacyMode ? () => '••••' : formatCompact}
                      width={45}
                    />
                    <Tooltip
                      formatter={(value: number) => isPrivacyMode ? '••••' : formatCurrency(value)}
                      contentStyle={{
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: '8px',
                        fontSize: '0.8125rem',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                      }}
                      cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                    />
                    <Legend wrapperStyle={{ paddingTop: 12 }} iconType="circle" iconSize={10} />
                    <Bar dataKey="income" name={t('common.income')} fill="url(#incomeGradient)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expense" name={t('common.expenses')} fill="url(#expenseGradient)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </>
      )}

      {/* Monthly Net Trend (was incorrectly labeled "Daily Balance") */}
      {visibility.dailyBalance && monthlyTrends.length > 0 && (
        <>
          <div className="section-head">
            <span className="section-title">{t('analytics.net_trend')}</span>
          </div>
          <Card className="chart-card">
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={140}>
                <AreaChart data={monthlyTrends}>
                  <defs>
                    <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#9d7aff" stopOpacity={0.35} />
                      <stop offset="50%" stopColor="#9d7aff" stopOpacity={0.12} />
                      <stop offset="100%" stopColor="#9d7aff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }}
                    tickFormatter={isPrivacyMode ? () => '••••' : formatCompact}
                    width={45}
                  />
                  <Tooltip
                    formatter={(value: number) => isPrivacyMode ? '••••' : formatCurrency(value)}
                    contentStyle={{
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: '8px',
                      fontSize: '0.8125rem',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                    }}
                    cursor={{ stroke: 'var(--accent)', strokeWidth: 1, strokeDasharray: '4 4' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="net"
                    name={t('analytics.summary.net')}
                    stroke="#9d7aff"
                    fill="url(#balanceGradient)"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 5, fill: '#9d7aff', stroke: '#0d0f14', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}

      {/* Top Income */}
      {visibility.topIncome && (
        <>
          <div className="section-head">
            <span className="section-title">{t('analytics.sections.topIncome.name')}</span>
            <span className="section-period">{dateRange.label}</span>
            <span className="section-total">
              <MoneyValue amount={data.totalIncome} privacy={isPrivacyMode} />
            </span>
          </div>
          <div className="income-breakdown-card">
            {loading ? (
              <div className="chart-loading">{t('common.loading')}</div>
            ) : data.incomeBreakdown.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">💰</span>
                <span className="empty-text">{t('analytics.top_income.empty')}</span>
                <span className="empty-hint">{t('analytics.empty_range_hint')}</span>
              </div>
            ) : (
              <>
                <div className="income-list">
                  {data.incomeBreakdown.slice(0, 10).map((item, index) => (
                    <div key={item.id} className="income-row">
                      <div className="income-rank">{index + 1}</div>
                      <div className="income-details">
                        <div className="income-amount">
                          <MoneyValue amount={item.amount} privacy={isPrivacyMode} />
                        </div>
                        <div className="income-meta">
                          <span className="income-account">{item.accountName}</span>
                          {item.description && (
                            <span className="income-desc">{item.description}</span>
                          )}
                        </div>
                      </div>
                      <div className="income-date">
                        {formatMonthDay(item.date)}
                      </div>
                      <div className="income-pct">{item.percentage.toFixed(0)}%</div>
                    </div>
                  ))}
                  {data.incomeBreakdown.length > 10 && (
                    <div className="income-more">
                      {t('analytics.top_income.more', { count: data.incomeBreakdown.length - 10 })}
                    </div>
                  )}
                </div>
                {/* Adjustments note - shown separately */}
                {data.adjustments.count > 0 && (
                  <div className="adjustments-note">
                    <span className="adjustments-label">{t('analytics.top_income.adjustments')}</span>
                    <span className="adjustments-value">
                      <MoneyValue amount={data.adjustments.net} privacy={isPrivacyMode} signed />
                    </span>
                    <span className="adjustments-count">{t('analytics.top_income.entries', { count: data.adjustments.count })}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
